import React, { forwardRef } from 'react';
import NavBadge from './NavBadge';

export type NavTabId = 'explore' | 'activity' | 'messages' | 'tools';

type NavTabProps = {
  id: NavTabId;
  label: string;
  /** Outline glyph, shown in the muted (inactive) state. */
  icon: React.ReactNode;
  /**
   * Filled glyph, cross-faded in while the tab is active. If omitted, the tab
   * keeps its outline glyph when active.
   */
  iconFilled?: React.ReactNode;
  isActive: boolean;
  onSelect: (id: NavTabId) => void;
  /** Unread count for the badge slot. <= 0 hides the badge. */
  badgeCount?: number;
  /** Localized accessible label for the button when a badge is shown. */
  badgeAriaLabel?: string;
};

/**
 * One tab cell of the bottom nav (JAA-266). Purely presentational — layout
 * differences across breakpoints (icon-above vs icon-beside) are CSS-only via
 * `.jaakko-nav`. The active mint marker is a single shared element owned by
 * Navigation that slides between tabs, so the button only forwards its ref for
 * measuring and never renders its own marker.
 *
 * Both glyphs are stacked in the icon box and cross-faded by CSS off
 * `.jaakko-nav__tab--active`, so the swap rides alongside the marker slide.
 */
const NavTab = forwardRef<HTMLButtonElement, NavTabProps>(
  (
    {
      id,
      label,
      icon,
      iconFilled,
      isActive,
      onSelect,
      badgeCount = 0,
      badgeAriaLabel,
    },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      className={`jaakko-nav__tab${isActive ? ' jaakko-nav__tab--active' : ''}`}
      aria-current={isActive ? 'page' : undefined}
      aria-label={badgeCount > 0 ? badgeAriaLabel : undefined}
      onClick={() => onSelect(id)}
    >
      <span className="jaakko-nav__icon">
        {icon}
        {iconFilled}
        {badgeCount > 0 && <NavBadge count={badgeCount} />}
      </span>
      <span className="jaakko-nav__label">{label}</span>
    </button>
  ),
);

NavTab.displayName = 'NavTab';

export default NavTab;
